'use client'

import { BUTTON_VARIANT } from '@fukku/button/Button.types'
import { ButtonContentLink } from '@fukku/button/ContentLink'
import type { DialogAPI } from '@fukku/dialog'
import { BottomSheet } from '@fukku/dialog/BottomSheet'
import { Content } from '@fukku/dialog/Content'
import { Header } from '@fukku/dialog/Header'
import { SideSheet, type SideSheetProps } from '@fukku/dialog/SideSheet'
import { dataTestIds } from 'pdp/src/constants/dataTestIds/dataTestIds'
import { useAnalytics } from 'pdp/src/contexts/AnalyticsProvider/AnalyticsProvider'
import { PdpActionEvents } from 'pdp/src/types/PdpActionEvents'
import {
	type ForwardRefExoticComponent,
	type ReactElement,
	type ReactNode,
	type RefAttributes,
	useRef,
} from 'react'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'
import { cx } from 'utils/cx'

import styles from './PropertiesWrapper.module.css'

type PropertiesWrapperProps = {
	readonly className?: string
	readonly children: ReactElement
	readonly title: string
}

export function PropertiesWrapper({
	className,
	children,
	title,
}: PropertiesWrapperProps): ReactNode {
	const modalRef = useRef<DialogAPI>(null)
	const { sendPdpActionEvent } = useAnalytics()

	const ResponsiveDialog = useResponsiveBehavior<
		ForwardRefExoticComponent<SideSheetProps & RefAttributes<DialogAPI>>
	>({
		default: SideSheet,
		small: BottomSheet,
	})

	const onPropertiesClick = (): void => {
		modalRef.current?.show()
		sendPdpActionEvent(PdpActionEvents.PRODUCT_CARE)
	}

	return (
		<div
			className={cx(className)}
			data-testid={
				dataTestIds['pdp.productDetails.compositionOriginAndCare.accordion']
			}
		>
			<ButtonContentLink
				variant={BUTTON_VARIANT.DEFAULT}
				className={cx(styles.button, className)}
				onClick={onPropertiesClick}
				data-testid={
					dataTestIds['pdp.productDetails.compositionOriginAndCare.content']
				}
			>
				{title}
			</ButtonContentLink>
			<ResponsiveDialog ref={modalRef} title={title}>
				<Header title={title} />
				<Content>{children}</Content>
			</ResponsiveDialog>
		</div>
	)
}
